import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { QUERY_KEYS } from 'constants/common';
import { SURVEY_TYPES } from 'constants/surveys';
import { useGetSurveyStatus } from 'hooks/useGetSurveyStatus';
import { useSaveSurveyResponse } from 'hooks/useSaveSurveyResponse';
import { type BarometerResponse } from 'pages/Pae/components/BarometerResults/types';
import { SurveyService } from 'services/surveys';
import { useTrackingEvents } from 'tracking/hooks/useTrackingEvents';
import { type SurveyType, type Question, type Survey, type SurveyResponse } from 'types/surveys';
import { getUserId } from 'utils/storage';
import { getSurveyQuestions } from 'utils/surveys';
import { trackBarometerSurveyCompleted } from './events';
import { getCurrentQuestionIndex } from './utils';

interface SurveyContextType {
  survey?: Survey;
  surveyResponse?: SurveyResponse;
  question?: Question;
  questions?: Question[];
  surveyType: SurveyType;
  isLoading: boolean;
  isCompleted: boolean;
  currentQuestionNumber: number;
  totalQuestions: number;
  isPending: boolean;
  saveResponse: (
    questionId: string,
    optionIds: string[],
    requiredTextValue: string | undefined
  ) => void;
  handleRepeat: () => Promise<void>;
}

const SurveyContext = createContext<SurveyContextType | undefined>(undefined);

interface SurveyProviderProps extends React.PropsWithChildren {
  surveyType: SurveyType;
  allowRepeat?: boolean;
  onCompleted?: () => void;
  surveyId?: string;
}

export const SurveyProvider = ({
  surveyType,
  allowRepeat = false,
  onCompleted,
  surveyId,
  children,
}: SurveyProviderProps): React.ReactElement => {
  const { survey, surveyResponse, questions, isLoading, isCompleted } = useGetSurveyStatus(
    surveyType,
    allowRepeat,
    surveyId
  );
  const queryClient = useQueryClient();
  const { t } = useTranslation(LOCIZE_NS.WELL_BEING);
  const question = questions?.[0];
  const { trackSurveyQuestionAnswered, trackDailyPulseCompleted, trackSurveyCompleted } =
    useTrackingEvents();

  const { mutate: saveSurveyResponse, isPending } = useSaveSurveyResponse(survey, {
    surveyType,
    onSuccess: data => {
      const completed = data.completed || data.isCompleted;

      if (completed) {
        if (surveyType === SURVEY_TYPES.TEST && survey) {
          trackBarometerSurveyCompleted(survey, data as BarometerResponse, trackSurveyCompleted);
        }

        onCompleted?.();
      }
    },
  });

  const trackQuestionResponse = useCallback(
    (currentQuestion: Question, optionIds: string[], requiredTextValue?: string) => {
      if (!survey) return;

      const selectedOptions = currentQuestion.optionsDTO.filter(option =>
        optionIds.includes(option.id)
      );
      const answerText = selectedOptions.map(option => option.text).join(', ');
      const score = selectedOptions.reduce((sum, opt) => sum + opt.score, 0);

      trackSurveyQuestionAnswered({
        question: currentQuestion.text,
        surveyType,
        category: currentQuestion.tag ?? survey.tag ?? undefined,
        answer: answerText,
        score,
        userId: getUserId(),
        requiredTextValue,
      });
      if (surveyType === SURVEY_TYPES.DAILY) {
        trackDailyPulseCompleted({
          responses: [score.toString()],
          survey,
          userId: getUserId(),
        });
      }
    },
    [survey, surveyType, trackSurveyQuestionAnswered, trackDailyPulseCompleted]
  );

  const saveResponse = useCallback(
    (questionId: string, optionIds: string[], requiredTextValue: string | undefined): void => {
      if (question && question.id === questionId) {
        trackQuestionResponse(question, optionIds, requiredTextValue);
      }

      saveSurveyResponse({ questionId, optionIds, requiredTextValue });
    },
    [saveSurveyResponse, question, trackQuestionResponse]
  );

  const response = surveyResponse ?? survey;
  const currentQuestionIndex = getCurrentQuestionIndex(response);
  const currentQuestionNumber = currentQuestionIndex + 1;
  const surveyQuestions = getSurveyQuestions(survey);
  const totalQuestions = useMemo(
    () => surveyQuestions?.length ?? questions?.length ?? Math.max(currentQuestionNumber, 1),
    [surveyQuestions?.length, questions?.length, currentQuestionNumber]
  );

  const handleRepeat = useCallback(async (): Promise<void> => {
    if (!survey?.id) return;
    try {
      await SurveyService.getSurveyResponses(survey.id);
      const queryKeys = [
        [QUERY_KEYS.FETCH_SURVEYS, surveyType],
        [QUERY_KEYS.FETCH_SURVEY_QUESTIONS, survey.id],
        [QUERY_KEYS.FETCH_SURVEY_RESPONSES, survey.id],
        [QUERY_KEYS.FETCH_DAILY_TRACKING_HISTORY, surveyType],
      ] as const;
      queryKeys.forEach(queryKey => {
        queryClient.invalidateQueries({ queryKey });
      });
    } catch {
      toast.error(t('WellBeing.Test.repeat.error'));
    }
  }, [survey?.id, surveyType, queryClient, t]);

  return (
    <SurveyContext.Provider
      value={{
        survey,
        surveyResponse,
        question,
        questions,
        surveyType,
        isLoading,
        isCompleted,
        currentQuestionNumber,
        totalQuestions,
        isPending,
        saveResponse,
        handleRepeat,
      }}
    >
      {children}
    </SurveyContext.Provider>
  );
};

export const useSurveyContext = (): SurveyContextType => {
  const context = useContext(SurveyContext);
  if (!context) {
    throw new Error('useSurveyContext must be used within SurveyProvider');
  }
  return context;
};
